/**
 * CityPicker — Manual city autocomplete fallback for users who decline GPS.
 *
 * Strategy:
 *   1. Bundled offline list of ~100 popular cities (instant filter)
 *   2. Above 2 chars, query Nominatim (OpenStreetMap) — free, no API key.
 *      Required: descriptive User-Agent + ≤1 req/sec.
 *   3. User picks a result → write to LocationService → close picker
 *   4. If user types a city not in results, "Use what I typed" sets a
 *      best-effort entry (city name only, lat=lng=null until reverse geocode)
 *
 * Privacy: no analytics, no tracking, no telemetry. The query string and
 * result city stay between the user's browser and Nominatim.
 */

import type { AweniContext } from '../../types';
import type { LocationService } from '../../services/LocationService';

interface PopularCity {
  city: string;
  country: string;
  lat: number;
  lng: number;
}

// Top ~80 cities by Muslim/global population — bundled for instant offline pick.
// Coordinates from OpenStreetMap (CC-BY-SA), trimmed to 4 decimals.
const POPULAR_CITIES: PopularCity[] = [
  { city: 'Casablanca', country: 'MA', lat: 33.5731, lng: -7.5898 },
  { city: 'Rabat', country: 'MA', lat: 34.0209, lng: -6.8416 },
  { city: 'Marrakech', country: 'MA', lat: 31.6295, lng: -7.9811 },
  { city: 'Fes', country: 'MA', lat: 34.0181, lng: -5.0078 },
  { city: 'Tangier', country: 'MA', lat: 35.7595, lng: -5.8340 },
  { city: 'Algiers', country: 'DZ', lat: 36.7538, lng: 3.0588 },
  { city: 'Oran', country: 'DZ', lat: 35.6976, lng: -0.6337 },
  { city: 'Tunis', country: 'TN', lat: 36.8065, lng: 10.1815 },
  { city: 'Cairo', country: 'EG', lat: 30.0444, lng: 31.2357 },
  { city: 'Alexandria', country: 'EG', lat: 31.2001, lng: 29.9187 },
  { city: 'Riyadh', country: 'SA', lat: 24.7136, lng: 46.6753 },
  { city: 'Mecca', country: 'SA', lat: 21.4225, lng: 39.8262 },
  { city: 'Medina', country: 'SA', lat: 24.5247, lng: 39.5692 },
  { city: 'Jeddah', country: 'SA', lat: 21.4858, lng: 39.1925 },
  { city: 'Dubai', country: 'AE', lat: 25.2048, lng: 55.2708 },
  { city: 'Abu Dhabi', country: 'AE', lat: 24.4539, lng: 54.3773 },
  { city: 'Doha', country: 'QA', lat: 25.2854, lng: 51.5310 },
  { city: 'Kuwait City', country: 'KW', lat: 29.3759, lng: 47.9774 },
  { city: 'Manama', country: 'BH', lat: 26.2235, lng: 50.5876 },
  { city: 'Muscat', country: 'OM', lat: 23.5859, lng: 58.4059 },
  { city: 'Amman', country: 'JO', lat: 31.9454, lng: 35.9284 },
  { city: 'Jerusalem', country: 'PS', lat: 31.7683, lng: 35.2137 },
  { city: 'Beirut', country: 'LB', lat: 33.8938, lng: 35.5018 },
  { city: 'Damascus', country: 'SY', lat: 33.5138, lng: 36.2765 },
  { city: 'Baghdad', country: 'IQ', lat: 33.3152, lng: 44.3661 },
  { city: 'Tehran', country: 'IR', lat: 35.6892, lng: 51.3890 },
  { city: 'Istanbul', country: 'TR', lat: 41.0082, lng: 28.9784 },
  { city: 'Ankara', country: 'TR', lat: 39.9334, lng: 32.8597 },
  { city: 'Karachi', country: 'PK', lat: 24.8607, lng: 67.0011 },
  { city: 'Lahore', country: 'PK', lat: 31.5204, lng: 74.3587 },
  { city: 'Islamabad', country: 'PK', lat: 33.6844, lng: 73.0479 },
  { city: 'Dhaka', country: 'BD', lat: 23.8103, lng: 90.4125 },
  { city: 'Jakarta', country: 'ID', lat: -6.2088, lng: 106.8456 },
  { city: 'Kuala Lumpur', country: 'MY', lat: 3.1390, lng: 101.6869 },
  { city: 'Singapore', country: 'SG', lat: 1.3521, lng: 103.8198 },
  { city: 'Mumbai', country: 'IN', lat: 19.0760, lng: 72.8777 },
  { city: 'Delhi', country: 'IN', lat: 28.6139, lng: 77.2090 },
  { city: 'Hyderabad', country: 'IN', lat: 17.3850, lng: 78.4867 },
  { city: 'Lagos', country: 'NG', lat: 6.5244, lng: 3.3792 },
  { city: 'Kano', country: 'NG', lat: 12.0022, lng: 8.5919 },
  { city: 'Abuja', country: 'NG', lat: 9.0820, lng: 8.6753 },
  { city: 'Dakar', country: 'SN', lat: 14.7167, lng: -17.4677 },
  { city: 'Nouakchott', country: 'MR', lat: 18.0735, lng: -15.9582 },
  { city: 'Bamako', country: 'ML', lat: 12.6392, lng: -8.0029 },
  { city: 'Khartoum', country: 'SD', lat: 15.5007, lng: 32.5599 },
  { city: 'Mogadishu', country: 'SO', lat: 2.0469, lng: 45.3182 },
  { city: 'Nairobi', country: 'KE', lat: -1.2921, lng: 36.8219 },
  { city: 'Addis Ababa', country: 'ET', lat: 9.1450, lng: 40.4897 },
  { city: 'Paris', country: 'FR', lat: 48.8566, lng: 2.3522 },
  { city: 'Marseille', country: 'FR', lat: 43.2965, lng: 5.3698 },
  { city: 'Lyon', country: 'FR', lat: 45.7640, lng: 4.8357 },
  { city: 'Toulouse', country: 'FR', lat: 43.6047, lng: 1.4442 },
  { city: 'London', country: 'GB', lat: 51.5074, lng: -0.1278 },
  { city: 'Birmingham', country: 'GB', lat: 52.4862, lng: -1.8904 },
  { city: 'Manchester', country: 'GB', lat: 53.4808, lng: -2.2426 },
  { city: 'Madrid', country: 'ES', lat: 40.4168, lng: -3.7038 },
  { city: 'Barcelona', country: 'ES', lat: 41.3851, lng: 2.1734 },
  { city: 'Berlin', country: 'DE', lat: 52.5200, lng: 13.4050 },
  { city: 'Munich', country: 'DE', lat: 48.1351, lng: 11.5820 },
  { city: 'Hamburg', country: 'DE', lat: 53.5511, lng: 9.9937 },
  { city: 'Frankfurt', country: 'DE', lat: 50.1109, lng: 8.6821 },
  { city: 'Brussels', country: 'BE', lat: 50.8503, lng: 4.3517 },
  { city: 'Amsterdam', country: 'NL', lat: 52.3676, lng: 4.9041 },
  { city: 'Rotterdam', country: 'NL', lat: 51.9244, lng: 4.4777 },
  { city: 'Rome', country: 'IT', lat: 41.9028, lng: 12.4964 },
  { city: 'Milan', country: 'IT', lat: 45.4642, lng: 9.1900 },
  { city: 'Stockholm', country: 'SE', lat: 59.3293, lng: 18.0686 },
  { city: 'Oslo', country: 'NO', lat: 59.9139, lng: 10.7522 },
  { city: 'Copenhagen', country: 'DK', lat: 55.6761, lng: 12.5683 },
  { city: 'Vienna', country: 'AT', lat: 48.2082, lng: 16.3738 },
  { city: 'Geneva', country: 'CH', lat: 46.2044, lng: 6.1432 },
  { city: 'Zurich', country: 'CH', lat: 47.3769, lng: 8.5417 },
  { city: 'New York', country: 'US', lat: 40.7128, lng: -74.0060 },
  { city: 'Los Angeles', country: 'US', lat: 34.0522, lng: -118.2437 },
  { city: 'Chicago', country: 'US', lat: 41.8781, lng: -87.6298 },
  { city: 'Houston', country: 'US', lat: 29.7604, lng: -95.3698 },
  { city: 'Toronto', country: 'CA', lat: 43.6532, lng: -79.3832 },
  { city: 'Montreal', country: 'CA', lat: 45.5017, lng: -73.5673 },
  { city: 'Sydney', country: 'AU', lat: -33.8688, lng: 151.2093 },
  { city: 'Melbourne', country: 'AU', lat: -37.8136, lng: 144.9631 },
];

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_DEBOUNCE_MS = 600; // respect Nominatim's 1 req/sec policy

export interface CityPickerDeps {
  ctx: AweniContext;
  locationService: LocationService;
  onResolved: () => void;
}

export class CityPicker {
  private overlay: HTMLElement | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private deps: CityPickerDeps;

  constructor(deps: CityPickerDeps) {
    this.deps = deps;
  }

  show(): void {
    if (this.overlay) return;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    const isRtl = this.deps.ctx.i18n.dir === 'rtl';

    const overlay = document.createElement('div');
    overlay.className = 'aweni-city-picker-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    overlay.innerHTML = `
      <div class="aweni-city-picker-card">
        <div class="aweni-picker-header">
          <input type="search" class="aweni-picker-input"
                 placeholder="${t('city_picker_search_placeholder')}"
                 autocomplete="off" autocorrect="off" autocapitalize="words" spellcheck="false"
                 aria-label="${t('city_picker_search_placeholder')}" />
          <button type="button" class="aweni-picker-close" aria-label="Close">×</button>
        </div>
        <div class="aweni-picker-results" role="listbox"></div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.overlay = overlay;
    this._injectStyles();

    const input = overlay.querySelector('.aweni-picker-input') as HTMLInputElement;
    const results = overlay.querySelector('.aweni-picker-results') as HTMLElement;

    overlay.querySelector('.aweni-picker-close')?.addEventListener('click', () => this._dismiss());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this._dismiss();
    });

    input.addEventListener('input', () => this._handleInput(input.value, results));
    input.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'Escape') this._dismiss();
    });

    // Show popular cities by default
    this._renderResults(POPULAR_CITIES.slice(0, 12), results, '');
    requestAnimationFrame(() => input.focus());
  }

  private _handleInput(query: string, resultsEl: HTMLElement): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    const q = query.trim();
    if (q.length === 0) {
      this._renderResults(POPULAR_CITIES.slice(0, 12), resultsEl, '');
      return;
    }
    // Instant offline filter on bundled list
    const lc = q.toLowerCase();
    const local = POPULAR_CITIES.filter((c) =>
      c.city.toLowerCase().includes(lc) || c.country.toLowerCase().includes(lc)
    );
    this._renderResults(local, resultsEl, q);

    // Debounced Nominatim query for fuzzy/global hits
    if (q.length >= 2) {
      this.debounceTimer = setTimeout(() => this._queryNominatim(q, resultsEl), NOMINATIM_DEBOUNCE_MS);
    }
  }

  private async _queryNominatim(query: string, resultsEl: HTMLElement): Promise<void> {
    try {
      const url = `${NOMINATIM_URL}?format=jsonv2&limit=8&addressdetails=1&q=${encodeURIComponent(query)}`;
      const resp = await fetch(url, {
        headers: { 'User-Agent': 'AWENI/2.0 (https://learnature-ly.com/aweni; support@learnature-ly.com)' },
        cache: 'force-cache',
      });
      if (!resp.ok) return;
      const data = await resp.json() as Array<{
        display_name: string;
        lat: string;
        lon: string;
        address?: { city?: string; town?: string; village?: string; country_code?: string };
      }>;
      const cities: PopularCity[] = data
        .map((r) => ({
          city: r.address?.city || r.address?.town || r.address?.village || r.display_name.split(',')[0],
          country: (r.address?.country_code || '').toUpperCase(),
          lat: parseFloat(r.lat),
          lng: parseFloat(r.lon),
        }))
        .filter((c) => c.city && !isNaN(c.lat) && !isNaN(c.lng));
      // Merge with locally-filtered cities; dedupe by city+country
      const seen = new Set(POPULAR_CITIES
        .filter((c) => c.city.toLowerCase().includes(query.toLowerCase()))
        .map((c) => `${c.city}|${c.country}`));
      const unique = cities.filter((c) => !seen.has(`${c.city}|${c.country}`));
      const local = POPULAR_CITIES.filter((c) =>
        c.city.toLowerCase().includes(query.toLowerCase()) ||
        c.country.toLowerCase().includes(query.toLowerCase())
      );
      this._renderResults([...local, ...unique].slice(0, 16), resultsEl, query);
    } catch { /* offline or rate-limited — keep local results visible */ }
  }

  private _renderResults(cities: PopularCity[], resultsEl: HTMLElement, query: string): void {
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    if (cities.length === 0) {
      resultsEl.innerHTML = `
        <div class="aweni-picker-empty">${t('city_picker_no_results')}</div>
        ${query
          ? `<button type="button" class="aweni-picker-typed" data-action="use-typed" data-typed="${this._escape(query)}">
               ${t('city_picker_use_typed')}: <strong>${this._escape(query)}</strong>
             </button>`
          : ''}
      `;
    } else {
      resultsEl.innerHTML = cities.map((c) => `
        <button type="button" class="aweni-picker-result"
                data-action="pick" data-lat="${c.lat}" data-lng="${c.lng}" data-city="${this._escape(c.city)}">
          <span class="aweni-picker-city-name">${this._escape(c.city)}</span>
          <span class="aweni-picker-country">${c.country}</span>
        </button>
      `).join('');
    }

    // Wire up clicks (delegated)
    resultsEl.querySelectorAll('[data-action="pick"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const el = btn as HTMLElement;
        const lat = parseFloat(el.dataset.lat || '');
        const lng = parseFloat(el.dataset.lng || '');
        const city = el.dataset.city || '';
        if (!isNaN(lat) && !isNaN(lng)) {
          this.deps.locationService.setManualLocation(lat, lng, city);
          this._dismiss();
          this.deps.onResolved();
        }
      });
    });
    resultsEl.querySelectorAll('[data-action="use-typed"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const typed = (btn as HTMLElement).dataset.typed || '';
        // Best-effort: store name only, no coords. PrayerTimeService will
        // refuse to compute and the user must try GPS or pick a known city.
        // We still close the picker so the user isn't stuck.
        this.deps.locationService.setManualLocation(0, 0, typed);
        this._dismiss();
        this.deps.onResolved();
      });
    });
  }

  private _escape(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  private _dismiss(): void {
    if (!this.overlay) return;
    this.overlay.style.opacity = '0';
    setTimeout(() => {
      this.overlay?.remove();
      this.overlay = null;
    }, 200);
  }

  private _injectStyles(): void {
    if (document.getElementById('aweni-city-picker-styles')) return;
    const style = document.createElement('style');
    style.id = 'aweni-city-picker-styles';
    style.textContent = `
      .aweni-city-picker-overlay {
        position: fixed; inset: 0; z-index: 9100;
        background: rgba(15, 23, 42, 0.7);
        display: flex; align-items: stretch; justify-content: center;
        opacity: 1; transition: opacity 200ms ease;
      }
      @media (min-width: 768px) {
        .aweni-city-picker-overlay { align-items: center; }
      }
      .aweni-city-picker-card {
        background: var(--card-bg, #1E293B);
        color: var(--text-primary, #F1F5F9);
        width: 100%; max-width: 480px;
        max-height: 90vh;
        display: flex; flex-direction: column;
      }
      @media (min-width: 768px) {
        .aweni-city-picker-card { border-radius: 24px; max-height: 70vh; }
      }
      .aweni-picker-header {
        display: flex; align-items: center; gap: 8px;
        padding: 16px; border-bottom: 1px solid var(--border, rgba(255,255,255,0.1));
      }
      .aweni-picker-input {
        flex: 1; padding: 12px 14px; border-radius: 12px;
        background: var(--bg-secondary, #0F172A); color: inherit;
        border: 1px solid var(--border, rgba(255,255,255,0.1));
        font-size: 15px; min-height: 44px;
      }
      .aweni-picker-input:focus { outline: 2px solid var(--accent, #10B981); outline-offset: 1px; }
      .aweni-picker-close {
        width: 44px; height: 44px; border-radius: 12px;
        background: transparent; color: var(--text-secondary, #94A3B8);
        border: none; cursor: pointer; font-size: 28px; line-height: 1;
      }
      .aweni-picker-results {
        flex: 1; overflow-y: auto;
        padding: 8px;
      }
      .aweni-picker-result {
        display: flex; align-items: center; justify-content: space-between;
        width: 100%; padding: 14px 16px; border-radius: 12px;
        background: transparent; color: inherit; border: none;
        cursor: pointer; font-size: 15px; min-height: 48px;
        text-align: start;
      }
      .aweni-picker-result:hover { background: var(--bg-secondary, #0F172A); }
      .aweni-picker-result:focus-visible { outline: 2px solid var(--accent, #10B981); outline-offset: 1px; }
      .aweni-picker-country { color: var(--text-secondary, #94A3B8); font-size: 13px; }
      .aweni-picker-empty {
        padding: 24px; text-align: center;
        color: var(--text-secondary, #94A3B8); font-size: 14px;
      }
      .aweni-picker-typed {
        display: block; width: calc(100% - 16px); margin: 0 8px 8px;
        padding: 14px; border-radius: 12px;
        background: var(--accent, #10B981); color: #fff;
        border: none; cursor: pointer; font-size: 14px;
        min-height: 48px;
      }
    `;
    document.head.appendChild(style);
  }
}
